/**
 * @file Paused state — in-game menu.
 *
 * Esc hierarchy: PLAYING → PAUSED → MENU (Esc), PAUSED → PLAYING (Enter).
 * Simulation freezes; dungeon presentation may still tick from Game.
 *
 * Esc from PAUSED no longer jumps straight to the title menu: it opens a
 * "quit to menu?" popup first, since leaving discards the current run.
 */

import { GameState } from './GameState.js';
import { GameStates } from './StateMachine.js';
import { Action } from '../../input/InputManager.js';
import { Events } from '../EventBus.js';

export class PausedState extends GameState {
  /**
   * @param {import('../Context.js').GameContext} ctx Shared game context.
   * @param {import('./StateMachine.js').StateMachine} machine Owning machine.
   */
  constructor(ctx, machine) {
    super(ctx, machine);
    /** @type {boolean} Whether the "quit to menu?" popup is up. @private */
    this._confirming = false;
    /** @type {(() => void) | null} @private */
    this._unsubscribeResponse = null;
    /** @type {(() => void) | null} @private */
    this._unsubscribeNav = null;
  }

  /** @returns {void} */
  enter() {
    this.ctx.audio.setSuspended(true);
    this.ctx.music.setSuspended(true);
    this._setConfirming(false);
    // The popup's own buttons answer through this rather than calling the
    // state machine directly, so the HUD never needs to know a state exists.
    this._unsubscribeResponse = this.ctx.events.on(Events.CONFIRM_QUIT_RESPONSE, ({ confirmed }) =>
      confirmed ? this._quitToMenu() : this._setConfirming(false)
    );
    // Same contract for the nav-list rows: Resume mirrors Enter, Main Menu
    // mirrors Esc (opens the same confirmation, never skips it).
    this._unsubscribeNav = this.ctx.events.on(Events.MENU_NAV_ACTION, ({ action }) => {
      if (this._confirming) return; // Popup is up; only its own buttons answer.
      if (action === 'resume') this.machine.change(GameStates.PLAYING);
      else if (action === 'mainMenu') this._setConfirming(true);
    });
  }

  /** @returns {void} */
  exit() {
    this.ctx.audio.setSuspended(false);
    this.ctx.music.setSuspended(false);
    this._setConfirming(false);
    this._unsubscribeResponse?.();
    this._unsubscribeResponse = null;
    this._unsubscribeNav?.();
    this._unsubscribeNav = null;
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const { input } = this.ctx;

    if (this._confirming) {
      // Esc cancels the popup rather than confirming it: a reflexive second
      // tap of the same key that opened it must never be the one that quits.
      if (input.justPressed(Action.PAUSE)) this._setConfirming(false);
      else if (input.justPressed(Action.CONFIRM)) this._quitToMenu();
      return;
    }

    if (input.justPressed(Action.PAUSE)) {
      this._setConfirming(true);
      return;
    }
    if (input.justPressed(Action.CONFIRM)) {
      this.machine.change(GameStates.PLAYING);
    }
  }

  /**
   * @param {boolean} value Whether the popup should be showing.
   * @returns {void}
   * @private
   */
  _setConfirming(value) {
    if (this._confirming === value) return;
    this._confirming = value;
    this.ctx.events.emit(Events.CONFIRM_QUIT_PROMPT, { visible: value });
  }

  /** @returns {void} @private */
  _quitToMenu() {
    this._setConfirming(false);
    this.machine.change(GameStates.MENU);
  }
}
